import {
  accountDeleteNotification,
  accountMarkAllNotificationsRead,
  accountMarkNotificationRead,
  accountPrependNotification,
  accountStore,
  accountUpdateNotificationsItems,
  accountUpdateNotificationsUnreadByEvent,
  accountUpdateNotificationsUnreadByType,
  accountUpdateNotificationsUnreadCount,
  notificationsRefreshUnread,
  notificationsRequest,
  selectApiKey,
  selectProfileId,
} from '@store/slices/account/accountSlice';
import { toastrShowSuccess } from '@store/toolkit/toastr/toastrActions';
import httpClient from '@utils/httpClient';
import { createNotificationChannel } from '@utils/signalrClient';
import { eventChannel } from 'redux-saga';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

// --- REST API workers ---

function* fetchNotificationsSaga() {
  try {
    const profileId = yield select(selectProfileId);
    if (!profileId) return;

    const { data } = yield call([httpClient, 'get'], `/api/profiles/${profileId}/notifications?take=50`);

    if (data.notifications) {
      // Response shape: { notifications: [...], unreadCount, unreadByType, unreadByEvent }
      yield put(accountUpdateNotificationsItems(data.notifications));
      yield put(accountUpdateNotificationsUnreadCount(data.unreadCount || 0));
      if (data.unreadByType) {
        yield put(accountUpdateNotificationsUnreadByType(data.unreadByType));
      }
      if (data.unreadByEvent) {
        yield put(accountUpdateNotificationsUnreadByEvent(data.unreadByEvent));
      }
    } else if (Array.isArray(data)) {
      // Fallback: response is a plain array
      yield put(accountUpdateNotificationsItems(data));
    }
  } catch (error) {
    console.error('fetchNotificationsSaga error:', error);
  }
}

function* fetchUnreadCountsSaga() {
  try {
    const profileId = yield select(selectProfileId);
    if (!profileId) return;

    const { data } = yield call([httpClient, 'get'], `/api/profiles/${profileId}/notifications/unread-counts`);

    yield put(accountUpdateNotificationsUnreadCount(data.total || 0));
    if (data.byType) {
      yield put(accountUpdateNotificationsUnreadByType(data.byType));
    }
    if (data.byEvent) {
      yield put(accountUpdateNotificationsUnreadByEvent(data.byEvent));
    }
  } catch (error) {
    console.error('fetchUnreadCountsSaga error:', error);
  }
}

function* markAsReadSaga({ payload: notificationId }) {
  try {
    const profileId = yield select(selectProfileId);
    if (!profileId) return;
    // Optimistic update already applied by the reducer
    yield call([httpClient, 'post'], `/api/profiles/${profileId}/notifications/${notificationId}/read`);
  } catch (error) {
    console.error('markAsReadSaga error:', error);
  }
}

function* markAllReadSaga() {
  try {
    const profileId = yield select(selectProfileId);
    if (!profileId) return;
    yield call([httpClient, 'post'], `/api/profiles/${profileId}/notifications/read`);
  } catch (error) {
    console.error('markAllReadSaga error:', error);
  }
}

function* deleteNotificationSaga({ payload: notificationId }) {
  try {
    const profileId = yield select(selectProfileId);
    if (!profileId) return;
    yield call([httpClient, 'delete'], `/api/profiles/${profileId}/notifications/${notificationId}`);
  } catch (error) {
    console.error('deleteNotificationSaga error:', error);
  }
}

// --- SignalR real-time ---

function* signalRSaga() {
  const apiKey = yield select(selectApiKey);
  if (!apiKey) return;

  let channel;
  try {
    channel = yield call(createNotificationChannel, apiKey);

    while (true) {
      const notification = yield take(channel);

      // Prepend to store (already normalized by signalrClient)
      // unreadByEvent counters are updated automatically by accountPrependNotification reducer
      yield put(accountPrependNotification(notification));

      // Handle display type
      const displayType = notification.notificationDisplay || 'badge';

      switch (displayType) {
        case 'toast':
          yield put(toastrShowSuccess(notification.title || notification.message));
          break;
        case 'badge':
        case 'feed':
          // Already prepended to store; UI updates via selectors
          break;
        case 'topbar':
          // Future: dispatch a topbar alert action
          break;
        default:
          break;
      }
    }
  } catch (error) {
    console.error('signalRSaga error:', error);
  } finally {
    if (channel) channel.close();
  }
}

// --- Tab focus: refresh unread counts when tab becomes visible ---

function createVisibilityChannel() {
  return eventChannel((emitter) => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        emitter('visible');
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  });
}

function* tabFocusSaga() {
  const channel = yield call(createVisibilityChannel);
  while (true) {
    yield take(channel);
    yield put(notificationsRefreshUnread());
  }
}

// --- Exported watchers (registered via root saga) ---

export function* notificationsFetchWatcher() {
  yield takeLatest(notificationsRequest, fetchNotificationsSaga);
}

export function* notificationsRefreshUnreadWatcher() {
  yield takeLatest(notificationsRefreshUnread, fetchUnreadCountsSaga);
}

export function* notificationsMarkReadWatcher() {
  yield takeEvery(accountMarkNotificationRead, markAsReadSaga);
}

export function* notificationsMarkAllReadWatcher() {
  yield takeLatest(accountMarkAllNotificationsRead, markAllReadSaga);
}

export function* notificationsDeleteWatcher() {
  yield takeEvery(accountDeleteNotification, deleteNotificationSaga);
}

export function* notificationsSignalRWatcher() {
  yield takeLatest(accountStore, signalRSaga);
}

export function* notificationsTabFocusWatcher() {
  yield call(tabFocusSaga);
}
